/**
 * Sidebar
 * Persistent dark-themed navigation sidebar used by Plan Wizard and Projection page.
 * Features: app branding, plan pills (MASTER/ALTERNATIVE), collapsible sections,
 * SVG icons, vertical progress line for wizard steps,
 * numbered step indicators with completion states, and plain item lists.
 * Collapses to a hamburger drawer on mobile (<768px).
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { CompareIcon } from './SidebarIcons';
import { SIDEBAR_ICONS, SECTION_ICONS } from './sidebarIconMap';
import './Sidebar.css';
import { parseStored, isBooleanRecord } from '../../utils/parseStored';

const COLLAPSED_STORAGE_KEY = 'sidebar-collapsed-sections';

export interface SidebarItem {
  id: string;
  label: string;
  hint?: string;
  number?: number;
  status?: 'active' | 'completed' | 'disabled';
  hidden?: boolean;
  badge?: 'changed';
}

export type ActivePlanView = 'primary' | 'alternative' | 'compare';

export interface PlanPillsConfig {
  activePlan: ActivePlanView;
  hasAlternative: boolean;
  onSelectPlan: (plan: 'primary' | 'alternative') => void;
  onCreateAlternative?: () => void;
  onCompare?: () => void;
  canCompare: boolean;
  onResetToPrimary?: () => void;
  pillTooltip?: (plan: 'primary' | 'alternative') => string;
}

export interface SidebarSection {
  id: string;
  label: string;
  subtitle?: string;
  items: SidebarItem[];
  status?: 'active' | 'disabled';
  collapsible?: boolean;
  defaultCollapsed?: boolean;
  dataTour?: string;
}

interface SidebarProps {
  sections: SidebarSection[];
  activeItemId: string;
  onItemClick: (itemId: string) => void;
  mobileOpen: boolean;
  onMobileClose: () => void;
  planPills?: PlanPillsConfig;
}

export function Sidebar({ sections, activeItemId, onItemClick, mobileOpen, onMobileClose, planPills }: Readonly<SidebarProps>) {
  // Track collapsed state per section id, persisted to sessionStorage
  // so switching plans (which remounts the page) preserves expand/collapse.
  const [collapsedSections, setCollapsedSections] = useState<Record<string, boolean>>(() => {
    // Try to restore from sessionStorage first
    const stored = parseStored(sessionStorage.getItem(COLLAPSED_STORAGE_KEY), isBooleanRecord);
    if (stored) return stored;
    // Fall back to defaultCollapsed from section config
    const initial: Record<string, boolean> = {};
    for (const section of sections) {
      if (section.collapsible) {
        initial[section.id] = section.defaultCollapsed ?? false;
      }
    }
    return initial;
  });

  // Persist to sessionStorage whenever collapsed state changes
  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    try {
      sessionStorage.setItem(COLLAPSED_STORAGE_KEY, JSON.stringify(collapsedSections));
    } catch { /* ignore */ }
  }, [collapsedSections]);

  const toggleSection = useCallback((sectionId: string) => {
    setCollapsedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId],
    }));
  }, []);

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape' && mobileOpen) {
      onMobileClose();
    }
  }, [mobileOpen, onMobileClose]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  const handleItemClick = (item: SidebarItem) => {
    if (item.status === 'disabled') return;
    onItemClick(item.id);
    onMobileClose();
  };

  const handleSectionClick = (section: SidebarSection) => {
    if (section.status === 'disabled') return;
    if (section.collapsible) {
      toggleSection(section.id);
      return;
    }
    onItemClick(section.id);
    onMobileClose();
  };

  return (
    <>
      {mobileOpen && (
        <div
          className="sidebar-backdrop"
          onClick={onMobileClose}
          aria-hidden="true"
        />
      )}
      <nav className={`sidebar ${mobileOpen ? 'sidebar--open' : ''}`} aria-label="Page navigation">
        {/* Branding */}
        <div className="sidebar-brand">
          <div className="sidebar-brand-logo">
            {/* The Horizon mark — see AppHeader.tsx; one identity across
                icon, header, sidebar and the iOS GPLogoMark. */}
            <svg width="30" height="30" viewBox="0 0 1024 1024" fill="none">
                <defs>
                  <clipPath id="gp-horizon-sidebar"><rect width="1024" height="1024" rx="224" /></clipPath>
                </defs>
                <g clipPath="url(#gp-horizon-sidebar)">
                  <rect width="1024" height="1024" fill="#5E6AD2" />
                  <circle cx="512" cy="465" r="135" fill="#FFFFFF" />
                  <rect y="465" width="1024" height="559" fill="#0A0F1F" />
                  <path fill="#FFFFFF" d="M300 1024C260 940 390 880 570 825C760 760 760 705 640 665C550 630 520 560 500 465H540C550 540 590 602 680 638C820 700 820 790 660 850C520 910 575 980 670 1024H300Z" />
                </g>
                <rect width="1024" height="1024" rx="224" fill="none" stroke="rgba(255,255,255,0.10)" strokeWidth="32" />
            </svg>
          </div>
          <div className="sidebar-brand-text">
            <span className="sidebar-brand-name">Glide-Path</span>
            <span className="sidebar-brand-tagline">Retirement Planning</span>
          </div>
        </div>

        {/* Plan pills — always visible when config provided */}
        {planPills && (
          <div className="sidebar-plan-pills-region">
            <div className="sidebar-plan-pills">
              <button
                type="button"
                className={`sidebar-plan-pill ${planPills.activePlan === 'primary' ? 'sidebar-plan-pill--primary-active' : ''} ${planPills.activePlan === 'compare' ? 'sidebar-plan-pill--compare-dim' : ''}`}
                onClick={() => planPills.onSelectPlan('primary')}
                title={planPills.pillTooltip?.('primary')}
              >
                My Plan
              </button>
              {planPills.hasAlternative ? (
                <button
                  type="button"
                  className={`sidebar-plan-pill ${planPills.activePlan === 'alternative' ? 'sidebar-plan-pill--alt-active' : ''} ${planPills.activePlan === 'compare' ? 'sidebar-plan-pill--compare-dim' : ''}`}
                  onClick={() => planPills.onSelectPlan('alternative')}
                  title={planPills.pillTooltip?.('alternative')}
                >
                  Alternative
                </button>
              ) : (
                planPills.onCreateAlternative && (
                  <button
                    type="button"
                    className="sidebar-plan-pill sidebar-plan-pill--create"
                    onClick={planPills.onCreateAlternative}
                  >
                    + Alternative
                  </button>
                )
              )}
            </div>
            {planPills.hasAlternative && planPills.onCompare && (
              <button
                type="button"
                className={`sidebar-item sidebar-compare-item ${planPills.activePlan === 'compare' ? 'active' : ''}`}
                onClick={planPills.onCompare}
                disabled={!planPills.canCompare}
                title={planPills.canCompare ? 'Compare both plans side-by-side' : 'Run projection for both plans first'}
              >
                <span className="sidebar-item-icon">
                  <CompareIcon />
                </span>
                <span className="sidebar-item-label">Compare Plans</span>
              </button>
            )}
            {planPills.hasAlternative && planPills.activePlan === 'alternative' && planPills.onResetToPrimary && (
              <button
                type="button"
                className="sidebar-reset-alt-btn"
                onClick={() => { onMobileClose(); planPills.onResetToPrimary?.(); }}
                title="Reset Alternative plan to match My Plan"
              >
                Reset Alternative to My Plan
              </button>
            )}
          </div>
        )}

        {/* Sections */}
        {sections.map(section => {
          const SectionIcon = SECTION_ICONS[section.id];
          const visibleItems = section.items.filter(item => !item.hidden);
          const hasNumberedItems = visibleItems.some(item => item.number != null);
          const isCollapsed = section.collapsible && collapsedSections[section.id];

          return (
            <div key={section.id} className="sidebar-section" data-tour={section.dataTour || undefined}>
              <button
                type="button"
                className={`sidebar-section-header ${section.status ?? ''}`}
                onClick={() => handleSectionClick(section)}
                disabled={section.status === 'disabled'}
                title={section.status === 'disabled' ? 'Complete all steps to generate a projection' : undefined}
                aria-expanded={section.collapsible ? !isCollapsed : undefined}
              >
                {SectionIcon && (
                  <span className="sidebar-section-icon">
                    <SectionIcon />
                  </span>
                )}
                <span className="sidebar-section-label-group">
                  {section.label}
                  {section.subtitle && (
                    <span className="sidebar-section-subtitle">{section.subtitle}</span>
                  )}
                </span>
                {section.collapsible && (
                  <span className={`sidebar-section-chevron ${isCollapsed ? 'sidebar-section-chevron--collapsed' : ''}`}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="6 9 12 15 18 9" />
                    </svg>
                  </span>
                )}
                {!section.collapsible && section.status !== 'disabled' && section.items.length === 0 && (
                  <span className="sidebar-section-arrow">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="5" y1="12" x2="19" y2="12" />
                      <polyline points="12 5 19 12 12 19" />
                    </svg>
                  </span>
                )}
              </button>

              {!isCollapsed && visibleItems.length > 0 && (
                <div className={`sidebar-items ${hasNumberedItems ? 'sidebar-items--stepped' : ''}`}>
                  {/* Progress line for wizard steps */}
                  {hasNumberedItems && (
                    <div className="sidebar-progress-track" aria-hidden="true">
                      {visibleItems.map((item, idx) => {
                        if (idx === visibleItems.length - 1) return null;
                        let segmentStatus: string;
                        if (item.status === 'completed' && visibleItems[idx + 1].status !== 'disabled') {
                          segmentStatus = 'completed';
                        } else if (item.status === 'active') {
                          segmentStatus = 'active';
                        } else {
                          segmentStatus = 'pending';
                        }
                        return (
                          <div
                            key={`seg-${item.id}`}
                            className={`sidebar-progress-segment sidebar-progress-segment--${segmentStatus}`}
                          />
                        );
                      })}
                    </div>
                  )}

                  {visibleItems.map(item => {
                    const isActive = item.id === activeItemId;
                    const statusClass = item.status ?? (isActive ? 'active' : '');
                    const IconComponent = SIDEBAR_ICONS[item.id];

                    return (
                      <button
                        key={item.id}
                        type="button"
                        className={`sidebar-item ${statusClass}`}
                        onClick={() => handleItemClick(item)}
                        disabled={item.status === 'disabled'}
                        aria-current={isActive ? 'step' : undefined}
                        aria-label={item.number ? `${item.label} (Step ${item.number})` : item.label}
                      >
                        {item.number != null && (
                          <span className="sidebar-item-number">
                            {item.number}
                          </span>
                        )}
                        {item.number == null && IconComponent && (
                          <span className="sidebar-item-icon">
                            <IconComponent />
                          </span>
                        )}
                        <span className="sidebar-item-label">
                          {item.label}
                          {item.hint && <span className="sidebar-item-hint">{item.hint}</span>}
                        </span>
                        {item.badge === 'changed' && (
                          <span className="sidebar-item-badge sidebar-item-badge--changed" aria-label="Changed from primary" title="Changed from My Plan" />
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </nav>
    </>
  );
}
